/**
 * Modèle responsable des informations métiers des événements pour une plateforme
 */
export class LiveEvents {
  private readonly globalEventType: GlobalEventType
  private readonly events: LiveEvent[]
  private readonly countEvents: Record<UniqueEventType, number>

  constructor({ globalEventType, events, countEvents }: { globalEventType: GlobalEventType; events: LiveEvent[]; countEvents: Record<UniqueEventType, number> }) {
    this.globalEventType = globalEventType
    this.events = events
    this.countEvents = countEvents
  }

  public getEvents(): LiveEvent[] {
    return this.events
  }

  public getGlobalEventType(): GlobalEventType {
    return this.globalEventType
  }

  public getEventsCount(): [string, number][] {
    return Object.entries(this.countEvents)?.filter(([_, eventNumber]) => eventNumber !== 0)
  }

  public hasEvents() {
    return this.events.length > 0
  }
}

export interface LiveEventsDto {
  agents: AgentDto[]
  incidents: IncidentDto[]
  requestedAgents: RequestedAgentDto[]
  contractors: ContractorDto[]
  helpers: HelperDto[]
  plans: PlanDto[]
  lineName: string
}

interface LiveEventDto {
  platformId: string
}

export type AgentDto = {
  arrivalDate: string
  team: string
  tetraNumber: string
} & LiveEventDto

export type IncidentDto = {
  typeLabel: string
  startTime: string
  eventId: string
} & LiveEventDto

export type RequestedAgentDto = {
  effective: number
  nomEquipe: string
} & LiveEventDto

export type ContractorDto = {
  contractorNumber: number
  teamLeaderNumber: number,
  startContractorInterventionTime: string,
  endContractorInterventionTime: string
} & LiveEventDto

export type HelperDto = {
  matricule: string
  arrivalTime: string
  department: string
} & LiveEventDto

export type PlanType = 'SPORT' | 'CULTURAL' | 'MANIFESTATION' | 'WORKS' | 'OTHER'

export type PlanDto = {
  title: string
  planType: PlanType
  startTime: string
  endTime: string
} & LiveEventDto

export type UniqueEventType = 'AGENT' | 'INCIDENT' | 'CONTRACTOR' | 'HELPER' | 'REQUESTED_AGENT'
type CompositeEventType = 'HELPERS_AND_AGENTS' | 'AGENTS_AND_CONTRACTORS' | 'AGENTS_AND_REQUESTED_AGENTS' | 'INCIDENTS_AND_OTHER'
export type GlobalEventType = UniqueEventType | CompositeEventType | PlanType
export type DetailEventType = UniqueEventType | PlanType

interface LiveEventBase {
  type: DetailEventType
  lineName: string
}

export type AgentEvent = AgentDto & LiveEventBase
export type IncidentEvent = IncidentDto & LiveEventBase
export type HelperEvent = HelperDto & LiveEventBase
export type ContractorEvent = ContractorDto & LiveEventBase
export type RequestedAgentEvent = RequestedAgentDto & LiveEventBase
export type PlanEvent = PlanDto & LiveEventBase

export type LiveEvent = AgentEvent | IncidentEvent | HelperEvent | ContractorEvent | RequestedAgentEvent | PlanEvent

/**
 * Factory responsable de la création d'un LiveEvents
 * Définit le type global avec les combinaisons possibles
 * Transforme les différents évènements en un modèle générique LiveEvent
 * Enregistre le nombre des évènements par type avant transformation
 */
export class LiveEventsFactory {
  private static readonly DETAIL_EVENT_TYPE_FROM_EVENT: Map<string, DetailEventType> = new Map<string, DetailEventType>(
    [
      [['AGENT'], 'AGENT'],
      [['REQUESTED_AGENT'], 'REQUESTED_AGENT'],
      [['CONTRACTOR'], 'CONTRACTOR'],
      [['INCIDENT'], 'INCIDENT'],
      [['HELPER'], 'HELPER'],
      [['AGENT', 'HELPER'], 'HELPERS_AND_AGENTS'],
      [['AGENT', 'CONTRACTOR'], 'AGENTS_AND_CONTRACTORS'],
      [['AGENT', 'REQUESTED_AGENT'], 'AGENTS_AND_REQUESTED_AGENTS'],
      [['AGENT', 'INCIDENT'], 'INCIDENTS_AND_OTHER'],
      [['INCIDENT', 'REQUESTED_AGENT'], 'INCIDENTS_AND_OTHER'],
      [['HELPER', 'INCIDENT'], 'INCIDENTS_AND_OTHER'],
      [['CONTRACTOR', 'INCIDENT'], 'INCIDENTS_AND_OTHER'],
      [['CONTRACTOR', 'INCIDENT', 'AGENT'], 'INCIDENTS_AND_OTHER'],
      [['CONTRACTOR', 'INCIDENT', 'HELPER'], 'INCIDENTS_AND_OTHER'],
      [['AGENT', 'INCIDENT', 'HELPER'], 'INCIDENTS_AND_OTHER'],
      [['AGENT', 'INCIDENT', 'HELPER', 'CONTRACTOR'], 'INCIDENTS_AND_OTHER'],
      [['SPORT'], 'SPORT'],
      [['CULTURAL'], 'CULTURAL'],
      [['WORKS'], 'WORKS'],
      [['MANIFESTATION'], 'MANIFESTATION'],
      [['OTHER'], 'OTHER'],
    ].map(([key, value]: [string[], DetailEventType]) => [LiveEventsFactory.normalizeKey(key), value])
  )

  private static normalizeKey(keys: string[]): string {
    return [...keys].sort().join('|')
  }

  public create({ platformId, liveEventDto }: { platformId: string; liveEventDto: LiveEventsDto }): LiveEvents {
    const filteredEvents = this.filterEventsByPlatformId(platformId, liveEventDto)
    const countEvents = this.countEvents(filteredEvents)
    return new LiveEvents({
      countEvents,
      globalEventType: this.getGlobalEventTypeFromEvents(countEvents),
      events: this.mergeEvents(filteredEvents),
    })
  }

  private filterEventsByPlatformId(platformId: string, liveEventDto: LiveEventsDto): LiveEventsDto {
    const eventFilterById = (event: LiveEventDto) => event.platformId === platformId
    return {
      agents: liveEventDto?.agents?.filter(eventFilterById) ?? [],
      incidents: liveEventDto?.incidents?.filter(eventFilterById) ?? [],
      helpers: liveEventDto?.helpers?.filter(eventFilterById) ?? [],
      contractors: liveEventDto?.contractors?.filter(eventFilterById) ?? [],
      requestedAgents: liveEventDto?.requestedAgents?.filter(eventFilterById) ?? [],
      plans: liveEventDto?.plans?.filter(eventFilterById) ?? [],
      lineName: liveEventDto?.lineName,
    }
  }

  private countEvents(liveEventDto: LiveEventsDto): Record<DetailEventType, number> {
    return {
      AGENT: liveEventDto.agents.length,
      INCIDENT: liveEventDto.incidents.length,
      HELPER: liveEventDto.helpers.length,
      REQUESTED_AGENT: liveEventDto.requestedAgents.length,
      CONTRACTOR: liveEventDto.contractors.length,
      CULTURAL: liveEventDto.plans.filter((plan) => plan.planType === 'CULTURAL').length,
      SPORT: liveEventDto.plans.filter((plan) => plan.planType === 'SPORT').length,
      WORKS: liveEventDto.plans.filter((plan) => plan.planType === 'WORKS').length,
      MANIFESTATION: liveEventDto.plans.filter((plan) => plan.planType === 'MANIFESTATION').length,
      OTHER: liveEventDto.plans.filter((plan) => plan.planType === 'OTHER').length,
    }
  }

  private getGlobalEventTypeFromEvents(countEvents: Record<UniqueEventType, number>): GlobalEventType {
    const event = Object.entries(countEvents)
      .filter(([, count]) => count > 0)
      .map(([eventType]) => eventType as UniqueEventType)
      .sort()

    return LiveEventsFactory.DETAIL_EVENT_TYPE_FROM_EVENT.get(LiveEventsFactory.normalizeKey(event)) ?? 'AGENT'
  }

  private calculateMinutes(dateS: string): number {
    const date = new Date(dateS)
    return date.getHours()*60 + date.getMinutes()
  }

  private mergeEvents({ lineName, agents, incidents, requestedAgents, helpers, contractors, plans }: LiveEventsDto): LiveEvent[] {
    const mapper = (type: DetailEventType, dto: AgentDto | IncidentDto | HelperDto | ContractorDto | RequestedAgentDto | PlanDto): LiveEvent => ({
      ...dto,
      type,
      lineName,
    })
    //filter for contractors
    if ( contractors.length > 0 ) {
      const nowInFrance = new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" });
      const nowInFranceDate = new Date(nowInFrance);
      const minutesNow = this.calculateMinutes(nowInFrance)-nowInFranceDate.getTimezoneOffset()
      contractors = contractors.filter(contractor => this.calculateMinutes(contractor.startContractorInterventionTime) <= minutesNow
        && this.calculateMinutes(contractor.endContractorInterventionTime) >= minutesNow)
    }
    return [
      ...agents.map((agent) => mapper('AGENT', agent)),
      ...incidents.map((incident) => mapper('INCIDENT', incident)),
      ...requestedAgents.map((requestedAgent) => mapper('REQUESTED_AGENT', requestedAgent)),
      ...contractors.map((contractor) => mapper('CONTRACTOR', contractor)),
      ...helpers.map((helper) => mapper('HELPER', helper)),
      ...plans.map((plan) => mapper(plan.planType, plan)),
    ]
  }
}
